import type { GridColDef } from '@mui/x-data-grid-pro'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import React, { ReactElement, useCallback, useMemo } from 'react'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { useTransactionsColumns } from 'src/components/TransactionsDataGrid/transactionsDataGrid.columns'
import type { TransactionsDataGridRowModel } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
// Shared with the stats roll-up so the tab has ONE navigation idiom and the
// modifier-click guard (⌘/Ctrl/Shift/Alt, non-primary button) lives in one
// place. TODO: promote out of CardOverviewStats if a third section needs it.
import StatNavLink from 'src/feature/CardOverviewStats/StatNavLink'
import { useTranslate } from 'src/localization/Trns'
import { useQueryTransactions } from 'src/queries/transactions.queries'

import * as style from './CardOverviewRecentTransactions.style'

/** The design shows five rows; the ticket caps it at five. */
export const RECENT_TRANSACTIONS_LIMIT = 5

/**
 * The six columns the Overview panel shows, in design order.
 *
 * Deliberately NOT expressed as a `ColConfig` entry in
 * `transactionsDataGrid.config.ts`, for two reasons:
 *
 *  1. That config cannot drop expense-category columns, which this panel must.
 *     `exclude` matches exact field names but EC fields are minted per category
 *     as `ec:<uuid>`, and omitting `EC_COLUMN_BLOCK` from `order` doesn't drop
 *     them either — `getConfiguredColumns` step 5 re-appends orphaned ECs. So an
 *     allowlist is required regardless, and one allowlist beats a config entry
 *     plus an allowlist.
 *  2. Registering a new `TransactionReportType` would also opt this panel into
 *     the filter-schema defaults and the saved-view namespace keyed off that
 *     enum — three subsystems a read-only summary doesn't use, and all four
 *     switches on it are non-exhaustive, so it would inherit them silently.
 *
 * The expense-category ("Account") column is out of scope until there is a
 * defined display rule for orgs with more than one category type — EX-40633.
 */
const OVERVIEW_COLUMN_FIELDS = [
  'date',
  'merchant',
  'cardUser',
  'card',
  'receipts',
  'amount',
] as const

/**
 * Distinct from the Transactions tab's list query so `useQueryFetchedTransactions`
 * — which reads the FIRST cached transactions-list key and warns when it finds
 * more than one — can tell them apart.
 */
const LOCATION_KEY = 'cardOverviewRecentTransactions'

/** `DataGrid` hard-codes `columnHeaderHeight={44}`. */
const HEADER_HEIGHT = 44
/**
 * `DataGrid` renders its grid at `height - 60`, an inset sized for the full-page
 * tables it was built for (toolbar + footer). This panel has neither — the
 * footer is hidden below — so the inset is added back rather than left as dead
 * space under the last row.
 */
const DATA_GRID_HEIGHT_INSET = 60

type CardOverviewRecentTransactionsProps = {
  className?: string
  creditCardId: string
  /**
   * Destination, built by the mounting page — features do not know URLs.
   *
   * `undefined` when the card has no Transactions tab to link to (a pending
   * card, for instance). The panel still renders its list: the transactions are
   * real and worth showing at any card state, and it is only the navigation
   * that has nowhere to go. The heading renders without the link.
   */
  viewAllHref?: string
  /** SPA navigation, supplied by the page. See `StatNavLink`. */
  onNavigate: (href: string) => void
  /**
   * `isSplit` rides along because selecting a split transaction must evict its
   * cached detail before navigating (the full grid does the same). Deciding what
   * selection *means* — evict, navigate — belongs to the page; this component
   * only reports that it happened.
   */
  onSelectTransaction: (transactionId: string, isSplit: boolean) => void
}

/**
 * The five most recent transactions for a credit card, for the redesigned
 * card-detail Overview tab (EX-40633).
 *
 * Reuses the Transactions tab's column factory rather than re-implementing
 * cells, so the date / merchant / card / receipt renderers stay in step with the
 * full grid. Routing-free by the `src/feature/**` boundary: the page supplies the
 * "view all" destination and the row-selection handler.
 */
const CardOverviewRecentTransactions = ({
  className,
  creditCardId,
  viewAllHref,
  onNavigate,
  onSelectTransaction,
}: CardOverviewRecentTransactionsProps): ReactElement => {
  const translate = useTranslate()

  const filters = useMemo(
    () => ({ creditCardId: [creditCardId], sort: '-date' }),
    [creditCardId],
  )

  const { data, isPending, isError } = useQueryTransactions({
    filters,
    perPage: RECENT_TRANSACTIONS_LIMIT,
    locationKey: LOCATION_KEY,
    // Overrides the list factory's `placeholderData: keepPreviousData`, which is
    // right for the full Transactions tab and wrong here. There, filters change
    // under a stable heading and a flash to empty is worse than briefly stale
    // rows. Here the heading IS the card: `AccountEntityPage` renders this from
    // a keyless <Route>, so moving between two card overviews re-renders rather
    // than remounts, and the previous card's merchants and amounts would paint
    // under the new card's heading with `isPending` already false.
    placeholderData: undefined,
  })

  // No date window: "five most recent" should not go blank on a quiet card.
  // Sliced as well as paged because `perPage` bounds the request, not the
  // flattened infinite-query result.
  const transactions = useMemo(
    () => (data?.transactions ?? []).slice(0, RECENT_TRANSACTIONS_LIMIT),
    [data?.transactions],
  )

  const rows = useMemo(
    () =>
      transactions.map((transaction, index) => ({
        id: transaction.id,
        transaction,
        index,
        // The row model carries precomputed expense-category cell data for the
        // full grid's `ec:*` columns. This panel renders none, so it is empty.
        expenseCategoryCellData: {},
      })),
    [transactions],
  )

  // No `context`: `pickConfig` falls back to the `default` ColConfig, which
  // returns every column. The allowlist below picks the six this panel shows.
  const { columns: allColumns } = useTransactionsColumns({ filters: {} })

  const columns = useMemo(
    () =>
      OVERVIEW_COLUMN_FIELDS.map((field) =>
        allColumns.find((column) => String(column.field) === field),
      )
        .filter(
          (column): column is GridColDef<TransactionsDataGridRowModel> =>
            column !== undefined,
        )
        // Read-only summary: the full grid makes expense-category and some other
        // cells click-to-edit, which would fire mutations from a panel that has
        // no way to surface their result.
        //
        // `sortable` goes the same way, and needs to be forced rather than
        // inherited: `receipts` is the one column the factory leaves sortable
        // (behind `receiptVerificationUiEnabled`), the shared grid pins
        // `sortingMode="server"`, and this panel passes no `onSortModelChange`.
        // Left alone, that header offers an arrow that reorders nothing —
        // `TransactionsDataGrid` gets away with it only because it owns a
        // `sortModel` and re-sorts client-side, none of which came along here.
        .map((column) => ({ ...column, editable: false, sortable: false })),
    [allColumns],
  )

  const selectRow = useCallback(
    (row: TransactionsDataGridRowModel): void => {
      onSelectTransaction(row.transaction.id, !!row.transaction.isSplit)
    },
    [onSelectTransaction],
  )

  const isEmpty = !isPending && !isError && rows.length === 0

  const body = (): ReactElement => {
    if (isPending) {
      return (
        <p css={style.statusBody} role="status">
          {translate('Loading transactions')}
        </p>
      )
    }

    if (isError) {
      return (
        <p css={style.statusBody} role="alert">
          {translate("We couldn't load recent transactions. Try again later.")}
        </p>
      )
    }

    if (isEmpty) {
      return (
        <p css={style.statusBody}>
          {translate('No transactions on this card yet.')}
        </p>
      )
    }

    return (
      <DataGrid<typeof filters, TransactionsDataGridRowModel>
        css={style.grid}
        rows={rows}
        columns={columns}
        filtersBody={filters}
        rowHeight={DataGridRowHeight.Default}
        height={
          HEADER_HEIGHT +
          rows.length * DataGridRowHeight.Default +
          DATA_GRID_HEIGHT_INSET
        }
        // Fixed column set, and all of it has to be said explicitly —
        // DataGridPro ships drag-reorder and resize on by default, so the
        // previous `disableColumnHiding` alone left two of the three live.
        //
        // `disableColumnMenu` rather than `disableColumnHiding`: it subsumes it,
        // and it is the only one that removes the ⋮ entirely. Hiding-only leaves
        // a menu holding just `GridColumnMenuSortItem`, which renders `null` for
        // a non-sortable column — so with every column forced `sortable: false`
        // above, the affordance would open an empty popover.
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        pinFirstColumn={false}
        checkboxSelection={false}
        // A five-row panel has no pagination and no selection, so MUI's default
        // footer contributes a "Total Rows: 5" line and ~52px of chrome that
        // says nothing the heading doesn't.
        hideFooter
        // Five rows never need windowing, and virtualization is what makes the
        // scroller's measured height wobble while it settles — which is what
        // makes page scroll catch on the table. Off, the rows are plain DOM.
        disableVirtualization
        sx={{
          // ⚠️ `DataGrid` merges consumer `sx` with a SHALLOW spread, so this
          // key REPLACES its `.MuiDataGrid-virtualScroller` rule rather than
          // extending it. Every property that rule sets has to be restated
          // here or it is silently dropped — `overscrollBehaviorX: 'none'`
          // most consequentially, because losing it lets a horizontal trackpad
          // swipe past the panel's left edge trigger browser BACK navigation.
          // This panel does scroll horizontally: its six columns' minWidths sum
          // to roughly 840px.
          //
          // Only `overscrollBehaviorY` and `overflowY` below are this panel's
          // own; the rest are the shared grid's, carried over verbatim.
          '& .MuiDataGrid-virtualScroller': {
            willChange: 'transform',
            transform: 'translateZ(0)',
            contain: 'layout paint',
            backfaceVisibility: 'hidden',
            overscrollBehaviorX: 'none',
            // The shared grid sets `contain` so a TALL, genuinely scrollable
            // table doesn't scroll the page out from under it. This panel is
            // never vertically scrollable, so that only gets in the way: any
            // sub-pixel scrollable range makes the scroller swallow wheel
            // events instead of letting the page take them.
            //
            // Note this does NOT restore native scroll chaining. `DataGrid`
            // still attaches `useTransferVerticalScroll`'s `{passive: false}`
            // wheel listener, and with `overflowY: 'hidden'` forced here its
            // `canConsumeVerticalScroll` is always false — so every wheel event
            // over the panel is preventDefault()ed and the ancestor is scrolled
            // from JS. It scrolls correctly, but without native momentum.
            // Removing that listener is the shared grid's call, not this
            // panel's.
            overscrollBehaviorY: 'auto',
            overflowY: 'hidden',
          },
        }}
        onRowClick={(params, event): void => {
          // A cell that handled the click itself (a link, a tooltip trigger)
          // preventDefaults; don't navigate out from under it.
          if (event.defaultPrevented) return
          selectRow(params.row)
        }}
        // Row navigation must not be mouse-only (src/CLAUDE.md WCAG AA floor).
        // The full Transactions grid gets away with click-only because its
        // chevron column is a bare SVG, not a link — so there is nothing to
        // inherit here. MUI gives cells a roving tabindex, so Enter on the
        // focused cell is the keyboard equivalent of clicking its row. Safe
        // against edit mode because every column above is `editable: false`.
        onCellKeyDown={(params, event): void => {
          if (event.key !== 'Enter' || event.defaultPrevented) return
          event.defaultMuiPrevented = true
          selectRow(params.row)
        }}
      />
    )
  }

  return (
    <ContentCard
      className={className}
      css={style.panel}
      data-testid="cardOverviewRecentTransactions"
    >
      <div css={style.header}>
        <h2 css={style.title}>{translate('Recent Transactions')}</h2>
        {viewAllHref !== undefined && (
          <StatNavLink href={viewAllHref} onNavigate={onNavigate}>
            {/* Arrow in the source string, matching every sibling link on the
                tab ("View budgets →", "Adjust limit →"). */}
            {translate('View all transactions →')}
          </StatNavLink>
        )}
      </div>
      {body()}
    </ContentCard>
  )
}

export default CardOverviewRecentTransactions
